import axios from "axios";

// Resolve API base URL with same-origin preference for production.
//
// Why: when the app is served from a custom domain (e.g. gextrade.fyi) but the
// build was made against a different backend URL (e.g. trader-gex-view.emergent.host),
// the resulting cross-origin requests get blocked by some ad-blockers, corporate
// firewalls and aggressive privacy extensions. Browsers are also stricter about
// 3rd-party cookies (auth) on those requests.
//
// Strategy:
//   1. If a build-time REACT_APP_BACKEND_URL is set AND its hostname matches the
//      current page's hostname → use it (single-origin, no issues).
//   2. If the page hostname differs from the build-time backend hostname
//      (we've been deployed to a custom domain) → use the current origin so
//      requests stay same-origin. The deployment proxy serves /api/* on the
//      same host already.
//   3. If REACT_APP_BACKEND_URL is unset → use current origin.
const resolveBackend = () => {
  const baked = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return baked;
  const pageHost = window.location.hostname;
  if (!baked) return window.location.origin;
  try {
    const bakedHost = new URL(baked).hostname;
    if (bakedHost === pageHost) return baked;
    // Mismatch → prefer same-origin (avoids cross-origin blocking on custom domains)
    return window.location.origin;
  } catch {
    return window.location.origin;
  }
};

const BACKEND_URL = resolveBackend();
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  timeout: 30000,
  withCredentials: true, // send cookies for auth
});

// Strip leading '/' so futures symbols like '/NQ' don't break URL path matching.
// Backend SYMBOL_MAP resolves bare 'NQ' → '/NQ' automatically.
const encodeSym = (s) => encodeURIComponent(String(s).replace(/^\//, ""));

export const fetchQuotes = () => client.get("/market/quotes").then((r) => r.data);
export const fetchGEX = (symbol) =>
  client.get(`/market/gex/${encodeSym(symbol)}`).then((r) => r.data);
// Unusual options flow snapshot — top scored contracts + summary stats.
export const fetchOptionsFlow = (symbol, limit = 25) =>
  client.get(`/market/flow/${encodeSym(symbol)}`, { params: { limit } }).then((r) => r.data);
// Cross-symbol "biggest unusual flow today" rollup for the Markets landing page.
export const fetchFlowLeaderboard = (limit = 12) =>
  client.get(`/market/flow-leaderboard`, { params: { limit } }).then((r) => r.data);
export const fetchStrategies = (symbol) =>
  client.get(`/strategies/${encodeSym(symbol)}`).then((r) => r.data);
export const fetchIV = (symbol) =>
  client.get(`/market/iv/${encodeSym(symbol)}`).then((r) => r.data);
export const fetchRecommendations = () =>
  client.get(`/recommendations`).then((r) => r.data);
export const fetchBrief = () => client.get("/market/brief").then((r) => r.data);
export const regenerateBrief = () => client.post("/market/brief").then((r) => r.data);
export const fetchOutlook = () => client.get("/market/outlook").then((r) => r.data);
export const fetchNews = (limit = 18) =>
  client.get(`/market/news?limit=${limit}`).then((r) => r.data);
export const fetchCompanyNews = (symbol, limit = 8) =>
  client.get(`/market/news/${encodeURIComponent(symbol)}?limit=${limit}`).then((r) => r.data);
export const fetchPremarket = () =>
  client.get("/market/premarket").then((r) => r.data);

// ---- Push notification helpers ----
export const fetchVapidKey = () => client.get("/push/vapid").then((r) => r.data);
export const fetchPushStatus = () => client.get("/push/status").then((r) => r.data);
export const subscribePush = (subscription) =>
  client.post("/push/subscribe", subscription).then((r) => r.data);
export const unsubscribePush = (endpoint) =>
  client.post("/push/unsubscribe", { endpoint }).then((r) => r.data);
export const getPushPreferences = (endpoint) =>
  client.get("/push/preferences", { params: { endpoint } }).then((r) => r.data);
export const updatePushPreferences = (endpoint, flow_alerts_enabled) =>
  client.patch("/push/preferences", { endpoint, flow_alerts_enabled }).then((r) => r.data);
export const fetchHeatmap = () => client.get("/analyze/heatmap").then((r) => r.data);
export const fetchAnalysis = (symbol) =>
  client.get(`/analyze/${encodeURIComponent(symbol)}`).then((r) => r.data);
export const fetchSavedAnalyses = () => client.get("/saved-analyses").then((r) => r.data);
export const createSavedAnalysis = (payload) =>
  client.post("/saved-analyses", payload).then((r) => r.data);
export const deleteSavedAnalysis = (id) =>
  client.delete(`/saved-analyses/${id}`).then((r) => r.data);

// ---- Price alerts ----
export const fetchPriceAlerts = () => client.get("/alerts/price").then((r) => r.data);
export const createPriceAlert = (payload) =>
  client.post("/alerts/price", payload).then((r) => r.data);
export const updatePriceAlert = (id, payload) =>
  client.patch(`/alerts/price/${id}`, payload).then((r) => r.data);
export const deletePriceAlert = (id) =>
  client.delete(`/alerts/price/${id}`).then((r) => r.data);
export const fetchBacktest = (symbol) =>
  client.get(`/strategies/${encodeSym(symbol)}/backtest`).then((r) => r.data);
export const fetchLeaderboard = () =>
  client.get(`/strategies/leaderboard`).then((r) => r.data);

// Admin (admin-only)
export const fetchAdminStats = () => client.get(`/admin/stats`).then((r) => r.data);
export const fetchAdminRecentLeads = (limit = 50) =>
  client.get(`/admin/recent-leads?limit=${limit}`).then((r) => r.data);
export const fetchAdminRecentUsers = (limit = 50) =>
  client.get(`/admin/recent-users?limit=${limit}`).then((r) => r.data);

// Push notifications
export const fetchPushPreview = () =>
  client.get(`/push/preview-today`).then((r) => r.data);
export const fireBestTargetNow = () =>
  client.post(`/push/fire-best-target-now`).then((r) => r.data);

// Equities (stocks + ETFs)
export const fetchEquitiesEducation = () =>
  client.get(`/equities/education`).then((r) => r.data);
export const fetchIncomeEtfs = () =>
  client.get(`/equities/income-etfs`).then((r) => r.data);
export const fetchEquitiesCompare = (symbols, period = "1y") =>
  client
    .get(`/equities/compare`, { params: { symbols: symbols.join(","), period } })
    .then((r) => r.data);
export const fetchEquityTradeIdeas = () =>
  client.get(`/equities/trade-ideas`).then((r) => r.data);
export const fetchTickerSearch = (q, limit = 8) =>
  client.get(`/equities/search`, { params: { q, limit } }).then((r) => r.data);

// Saved Collar Plans (Pro only)
export const listCollarPlans = () =>
  client.get(`/collar-plans`).then((r) => r.data);
export const createCollarPlan = (payload) =>
  client.post(`/collar-plans`, payload).then((r) => r.data);
export const deleteCollarPlan = (planId) =>
  client.delete(`/collar-plans/${planId}`).then((r) => r.data);
export const updateCollarPlanAutoRoll = (planId, payload) =>
  client.patch(`/collar-plans/${planId}/auto-roll`, payload).then((r) => r.data);
export const rollCollarPlanNow = (planId) =>
  client.post(`/collar-plans/${planId}/roll-now`).then((r) => r.data);

// Live ticker info for the Collar tool (spot + dividend yield, comma-separated)
export const fetchCollarTickerInfo = (symbols) =>
  client.get(`/collar/ticker-info`, {
    params: { symbols: Array.isArray(symbols) ? symbols.join(",") : symbols },
  }).then((r) => r.data);

// Live per-leg liquidity + available expirations for the Collar builder.
export const fetchCollarContractDetail = (params) =>
  client.get(`/collar/contract-detail`, { params }).then((r) => r.data);

// Top Income Collars leaderboard
export const fetchCollarLeaderboard = (profile = "balanced", shares = 100, limit = 50, minOi = 0) =>
  client.get(`/collar/leaderboard`, { params: { profile, shares, limit, min_oi: minOi } }).then((r) => r.data);

// Zero-cost collar solver
export const fetchZeroCostCollar = (params) =>
  client.get(`/collar/zero-cost`, { params }).then((r) => r.data);

// Earnings / ex-div event guard
export const fetchCollarEvents = (symbol, dte = 30) =>
  client.get(`/collar/events`, { params: { symbol, dte } }).then((r) => r.data);

// Backtest engine
export const runCollarBacktest = (params) =>
  client.get(`/collar/backtest`, { params }).then((r) => r.data);
export const fetchCompareDetailed = (symbols) =>
  client
    .get(`/equities/compare-detailed`, { params: { symbols: symbols.join(",") } })
    .then((r) => r.data);
export const calculateEtfMix = (weights) =>
  client.post(`/equities/mix`, { weights }).then((r) => r.data);

// Indicators (KCE)
export const fetchIndicators = () =>
  client.get(`/indicators`).then((r) => r.data);
export const fetchIndicatorScan = (slug, opts = {}) => {
  const params = new URLSearchParams();
  if (opts.symbols) params.set("symbols", opts.symbols.join(","));
  if (opts.only_active) params.set("only_active", "true");
  return client
    .get(`/indicators/${slug}/scan${params.toString() ? `?${params}` : ""}`)
    .then((r) => r.data);
};
export const fetchIndicatorCompute = (slug, symbol) =>
  client.get(`/indicators/${slug}/compute?symbol=${encodeURIComponent(symbol)}`).then((r) => r.data);
export const fetchIndicatorPreview = (slug, symbol) =>
  client.get(`/indicators/${slug}/preview?symbol=${encodeURIComponent(symbol)}`).then((r) => r.data);

// Profile
export const updateProfile = (payload) =>
  client.patch(`/auth/profile`, payload).then((r) => r.data);

// Discord webhook for Pro flow alerts
export const fetchDiscordStatus = () =>
  client.get(`/push/discord/status`).then((r) => r.data);
export const testDiscordWebhook = (webhook_url) =>
  client.post(`/push/discord/test`, webhook_url ? { webhook_url } : {}).then((r) => r.data);

// Admin · Stripe webhook health — Stripe billing was retired; helpers removed.

// Admin · TradingView Invite-Only access
export const fetchTvUsers = () =>
  client.get(`/admin/tv-users`).then((r) => r.data);
export const grantTvAccess = (user_id) =>
  client.post(`/admin/tv-users/${encodeURIComponent(user_id)}/grant`).then((r) => r.data);
export const revokeTvAccess = (user_id) =>
  client.post(`/admin/tv-users/${encodeURIComponent(user_id)}/revoke`).then((r) => r.data);

// KCE chart series — leaps radar helpers first, then KCE below.
export const fetchLeapsRadar = (symbols) => {
  const params = symbols ? { symbols: symbols.join(",") } : {};
  return client.get(`/leaps/radar`, { params }).then((r) => r.data);
};
export const fetchLeapsSingle = (symbol) =>
  client.get(`/leaps/${encodeURIComponent(symbol)}`).then((r) => r.data);

// KCE chart series (Pro-only). `settings` is an optional object with keys:
// { vapor_period, vapor_ob, vapor_os, ema_fast, ema_slow } — any subset.
// Values are sent as query params so the backend can override the engine
// defaults per call.
export const fetchKCESeries = (symbol, lookback = 120, settings = null) => {
  const params = { symbol, lookback };
  if (settings) {
    for (const k of ["vapor_period", "vapor_ob", "vapor_os", "ema_fast", "ema_slow"]) {
      if (settings[k] != null) params[k] = settings[k];
    }
  }
  return client
    .get(`/indicators/kce/series`, { params })
    .then((r) => r.data);
};

// Generic OHLC bars for any ticker (used by candlestick charts)
export const fetchOHLC = (symbol, period = "1y") =>
  client
    .get(`/market/ohlc/${encodeURIComponent(symbol)}`, { params: { period } })
    .then((r) => r.data);

// Saved Mixes (Pro tier)
export const listSavedMixes = () =>
  client.get(`/equities/saved-mixes`).then((r) => r.data);
export const createSavedMix = (payload) =>
  client.post(`/equities/saved-mixes`, payload).then((r) => r.data);
export const patchSavedMix = (mix_id, payload) =>
  client.patch(`/equities/saved-mixes/${mix_id}`, payload).then((r) => r.data);
export const deleteSavedMix = (mix_id) =>
  client.delete(`/equities/saved-mixes/${mix_id}`).then((r) => r.data);
export const fetchSavedMixPerf = (mix_id) =>
  client.get(`/equities/saved-mixes/${mix_id}/perf`).then((r) => r.data);

// Smart Money / Copy Trading
export const fetchTraders = (category) =>
  client
    .get(`/smart-money/traders`, { params: category ? { category } : {} })
    .then((r) => r.data);
export const fetchSmartTrades = (trader_id, limit = 50) =>
  client
    .get(`/smart-money/trades`, { params: { trader_id, limit } })
    .then((r) => r.data);
export const fetchTraderDetail = (trader_id) =>
  client.get(`/smart-money/trader/${trader_id}`).then((r) => r.data);
export const followTrader = (trader_id) =>
  client.post(`/smart-money/follow`, { trader_id }).then((r) => r.data);
export const unfollowTrader = (trader_id) =>
  client.delete(`/smart-money/follow/${trader_id}`).then((r) => r.data);
export const fetchMyFollows = () =>
  client.get(`/smart-money/my-follows`).then((r) => r.data);
export const fetchSmartMoneyRanking = (opts = {}) => {
  const { category = null, limit = 10, sort_by = "volume", min_trades = 0 } = opts;
  const params = { limit, sort_by };
  if (category) params.category = category;
  if (min_trades) params.min_trades = min_trades;
  return client.get(`/smart-money/ranking`, { params }).then((r) => r.data);
};
export const fetchSmartMoneyLiveFeed = (limit = 30, sinceDisclosure = null) =>
  client.get(`/smart-money/live-feed`, {
    params: { limit, ...(sinceDisclosure ? { since_disclosure: sinceDisclosure } : {}) },
  }).then((r) => r.data);

// Chart-layouts CRUD (Pro-only)
export const listChartLayouts = () =>
  client.get(`/chart-layouts`).then((r) => r.data);
export const createChartLayout = (payload) =>
  client.post(`/chart-layouts`, payload).then((r) => r.data);
export const deleteChartLayout = (id) =>
  client.delete(`/chart-layouts/${encodeURIComponent(id)}`).then((r) => r.data);
export const renameChartLayout = (id, payload) =>
  client.patch(`/chart-layouts/${encodeURIComponent(id)}`, payload).then((r) => r.data);

// Multi-chart workspace CRUD (Pro-only)
export const listWorkspaces = () =>
  client.get(`/chart-workspaces`).then((r) => r.data);
export const createWorkspace = (payload) =>
  client.post(`/chart-workspaces`, payload).then((r) => r.data);
export const updateWorkspace = (id, payload) =>
  client.patch(`/chart-workspaces/${encodeURIComponent(id)}`, payload).then((r) => r.data);
export const deleteWorkspace = (id) =>
  client.delete(`/chart-workspaces/${encodeURIComponent(id)}`).then((r) => r.data);
export const fetchSmartMoneyRecall = (limit = 100, days = 14) =>
  client.get(`/smart-money/recall`, { params: { limit, days } }).then((r) => r.data);
export const createRecallShare = (title, days = 14) =>
  client.post(`/smart-money/recall/share`, { title, days }).then((r) => r.data);
export const fetchRecallShare = (id) =>
  client.get(`/smart-money/recall/share/${encodeURIComponent(id)}`).then((r) => r.data);
export const fetchInsiderForSymbol = (symbol) =>
  client.get(`/smart-money/insider/${encodeURIComponent(symbol)}`).then((r) => r.data);
export const fetchSmartMoneyForTicker = (symbol) =>
  client.get(`/smart-money/ticker/${encodeURIComponent(symbol)}`).then((r) => r.data);

// Leads (email capture) — public
export const captureLead = (payload) => client.post("/leads", payload).then((r) => r.data);

// Auth
export const authSession = (session_id, ref_code) =>
  client.post("/auth/session", { session_id, ref_code }).then((r) => r.data);
export const authMe = () => client.get("/auth/me").then((r) => r.data);
export const authLogout = () => client.post("/auth/logout").then((r) => r.data);

// Watchlist (auth-gated)
export const getWatchlist = () => client.get("/watchlist").then((r) => r.data);
export const addWatchlist = (symbol, note) =>
  client.post("/watchlist", { symbol, note }).then((r) => r.data);
export const removeWatchlist = (symbol) =>
  client.delete(`/watchlist/${encodeSym(symbol)}`).then((r) => r.data);

// Saved recos (auth-gated)
export const getSavedRecos = () => client.get("/saved-recos").then((r) => r.data);
export const saveReco = (reco) =>
  client.post("/saved-recos", {
    symbol: reco.symbol,
    action: reco.action,
    confidence: reco.confidence,
    price: reco.price,
  }).then((r) => r.data);
export const removeSavedReco = (symbol) =>
  client.delete(`/saved-recos/${encodeSym(symbol)}`).then((r) => r.data);

// Billing (Pro tier) — Stripe billing was retired when the app went free.
// Legacy helpers kept as no-ops for any straggler imports.
export const fetchBillingConfig = () =>
  Promise.resolve({ price_usd: 0, currency: "usd", perks: [] });
export const fetchBillingPreview = () =>
  Promise.resolve({ final_amount_usd: 0 });
export const fetchSubscription = () =>
  Promise.resolve({ tier: "pro", pro_since: null, last_payment_status: null });

export const SYMBOLS = [
  { key: "SPX", label: "S&P 500", futures: "/ES", proxy: "^GSPC" },
  { key: "/ES", label: "E-mini S&P", futures: "/ES", proxy: "ES=F" },
  { key: "/NQ", label: "Nasdaq 100", futures: "/NQ", proxy: "NQ=F" },
  { key: "/GC", label: "Gold", futures: "/GC", proxy: "GC=F" },
  { key: "/MCL", label: "Crude Oil", futures: "/MCL", proxy: "MCL=F" },
  { key: "/ZN", label: "10-Yr Note", futures: "/ZN", proxy: "ZN=F" },
];

// ---------------------------------------------------------------------------
// Markets Pro — screeners, calendar (IPOs / new ETFs), scanners (LEAPs/Income)
// ---------------------------------------------------------------------------
export const fetchScreenerCategories = () =>
  client.get("/markets-pro/screeners/categories").then((r) => r.data);
export const fetchScreener = (category) =>
  client.get(`/markets-pro/screeners/${category}`).then((r) => r.data);
export const fetchIpoCalendar = (daysBack = 30, daysForward = 90) =>
  client.get(`/markets-pro/calendar/ipos`, { params: { days_back: daysBack, days_forward: daysForward } }).then((r) => r.data);
export const fetchNewEtfs = (days = 730) =>
  client.get(`/markets-pro/calendar/new-etfs`, { params: { days } }).then((r) => r.data);
export const fetchLeapsScan = (params = {}) =>
  client.get(`/markets-pro/scanners/leaps`, { params }).then((r) => r.data);
export const fetchIncomeScan = (params = {}) =>
  client.get(`/markets-pro/scanners/income`, { params }).then((r) => r.data);
export const fetchSectorFocus = () =>
  client.get(`/markets-pro/insights/sector-focus`).then((r) => r.data);
export const fetchSectorDrilldown = (sectorSymbol) =>
  client.get(`/markets-pro/insights/sector-focus/${sectorSymbol}`).then((r) => r.data);
export const fetchCheckpoints = () =>
  client.get(`/markets-pro/insights/checkpoints`).then((r) => r.data);
export const fetchInstitutionalFootprint = (minVolumeRatio = 1.3, limit = 25) =>
  client
    .get(`/markets-pro/insights/institutional-footprint`, {
      params: { min_volume_ratio: minVolumeRatio, limit },
    })
    .then((r) => r.data);
export const fetchBestTargets = (limit = 25, confirmAll = false) =>
  client
    .get(`/markets-pro/insights/best-targets`, { params: { limit, confirm_all: confirmAll } })
    .then((r) => r.data);
export const fetchVolatilityLab = () =>
  client.get(`/volatility-lab/dashboard`).then((r) => r.data);
export const fetchFearGreed = () =>
  client.get(`/fear-greed/index`).then((r) => r.data);
export const fetchSentimentDivergence = () =>
  client.get(`/fear-greed/divergence`).then((r) => r.data);

// ---------------------------------------------------------------------------
// Macro signals — military contracts, Google Trends, Reddit trends
// ---------------------------------------------------------------------------
export const fetchMilitaryContracts = (daysBack = 30, limit = 50, minAwardUsd = 50_000_000) =>
  client.get(`/macro-signals/military-contracts`, { params: { days_back: daysBack, limit, min_award_usd: minAwardUsd } }).then((r) => r.data);
export const fetchGoogleTrends = (keywords, timeframe = "today 3-m") =>
  client.get(`/macro-signals/google-trends`, { params: { keywords, timeframe } }).then((r) => r.data);
export const fetchRedditTrends = () =>
  client.get(`/macro-signals/reddit-trends`).then((r) => r.data);
export const fetchSpyEsBasis = () =>
  client.get(`/markets-pro/basis/spy-es`).then((r) => r.data);
export const fetchInboxHealth = () =>
  client.get(`/admin/inbox-health`).then((r) => r.data);

// Screener presets (Pro feature)
export const fetchScreenerPresets = (category) =>
  client.get(`/screener-presets`, { params: { category } }).then((r) => r.data);
export const createScreenerPreset = (payload) =>
  client.post(`/screener-presets`, payload).then((r) => r.data);
export const deleteScreenerPreset = (presetId) =>
  client.delete(`/screener-presets/${presetId}`).then((r) => r.data);
export const fetchScreenerPresetsAvailability = () =>
  client.get(`/screener-presets/availability`).then((r) => r.data);

// Barbell Builder + Just Keep Buying (Markets Pro)
export const fetchBarbellPresets = () =>
  client.get(`/markets-pro/barbell/presets`).then((r) => r.data);
export const runBarbellBacktest = (payload) =>
  client.post(`/markets-pro/barbell/backtest`, payload, { timeout: 90000 }).then((r) => r.data);
export const runJkbBacktest = (payload) =>
  client.post(`/markets-pro/jkb/backtest`, payload, { timeout: 90000 }).then((r) => r.data);

// Income Vision (Vault)
export const analyzeIncomeHoldings = (holdings, drip = true) =>
  client.post(`/income-vision/analyze`, { holdings, drip }, { timeout: 90000 }).then((r) => r.data);
export const fetchVaultIncomeVision = (drip = true) =>
  client.get(`/income-vision/vault`, { params: { drip }, timeout: 90000 }).then((r) => r.data);
